import { db } from '../firebase-config';
import { collection, addDoc, getDocs, getDoc, updateDoc, deleteDoc, doc, query, where, Timestamp} from 'firebase/firestore';
import { isOverdue, isUpcomingAccra, formatAccraDate } from '../utils/firmassistTime';
import { enforceCanonicalDueTime } from '../utils/dueTimeValidation';

/**
 * Enhanced Task Service
 * Supports: Tasks, Court Dates, Deadlines with time, location, and assignments
 */

// Create Enhanced Task
export const createEnhancedTask = async (userId, taskData, firmId) => {
  try {
    // Write-boundary enforcement: dueTime must be canonical "HH:MM" (or
    // blank). A well-formed legacy 12-hour value is normalized
    // deterministically; anything ambiguous/malformed is rejected before
    // any write is attempted — see utils/dueTimeValidation.js.
    const dueTimeCheck = enforceCanonicalDueTime(taskData.dueTime);
    if (!dueTimeCheck.ok) {
      return { success: false, error: dueTimeCheck.error };
    }

    const tasksRef = collection(db, 'tasks');
    const newTask = {
      userId,
      firmId: firmId,
      title: taskData.title,
      description: taskData.description || '',
      caseId: taskData.caseId || null,
      caseName: taskData.caseName || '',
      
      // Enhanced Fields
      eventType: taskData.eventType || 'task', // 'task', 'court_date', 'deadline'
      assignedTo: taskData.assignedTo || '', // Display-name snapshot — UI/backward-compat ONLY.
      // Canonical stable identity for notification recipient resolution
      // (FirmAssist Notification Orchestration Layer, Phase 1 hardening) —
      // a real Firebase Auth uid, set whenever the assignee was picked from
      // the firm-member dropdown. May be null for legacy records or when
      // "Unassigned" is selected; assignedTo (the name) is never the
      // preferred identity once this is set.
      assignedToId: taskData.assignedToId || null,

      // Client hearing notifications (Phase 2) — explicit opt-in only.
      // Must be exactly `true` to enable; any other value (missing,
      // false, undefined) defaults OFF so existing hearings never become
      // client-notification-eligible just by this field being deployed.
      notifyClient: taskData.notifyClient === true,

      // Date & Time
      dueDate: taskData.dueDate, // Date string (YYYY-MM-DD)
      dueTime: dueTimeCheck.value, // Time string (HH:MM), canonical — optional
      
      // Location (for court dates)
      location: taskData.location || '',
      
      // Court-specific fields
      court: taskData.court || '',
      judge: taskData.judge || '',
      hearingType: taskData.hearingType || '',
      clientPhone: taskData.clientPhone || '',
      
      // Status & Priority
      status: taskData.status || 'pending', // 'pending', 'in-progress', 'completed'
      priority: taskData.priority || 'medium', // 'high', 'medium', 'low'
      
      // Notes
      notes: taskData.notes || '',
      
      // External sync
      source: taskData.source || '',
      googleCalendarEventId: taskData.googleCalendarEventId || '',
      
      // Metadata
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      completedAt: null
    };

    const docRef = await addDoc(tasksRef, newTask);
    
    return {
      success: true,
      taskId: docRef.id,
      task: { id: docRef.id, ...newTask }
    };
  } catch (error) {
    console.error('Error creating enhanced task:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get All Enhanced Tasks
export const getEnhancedTasks = async (userId, filters = {}, firmId) => {
  try {
    const tasksRef = collection(db, 'tasks');
    let tasks = [];

    // Get firmId records
    if (firmId) {
      let q = query(tasksRef, where('firmId', '==', firmId));
      if (filters.eventType) q = query(q, where('eventType', '==', filters.eventType));
      if (filters.status) q = query(q, where('status', '==', filters.status));
      if (filters.caseId) q = query(q, where('caseId', '==', filters.caseId));
      const snapshot = await getDocs(q);
      tasks = snapshot.docs.map(doc => ({
        id: doc.id, ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        completedAt: doc.data().completedAt?.toDate()
      }));
    }

    // No creator-field (userId-only) fallback: tasks are read ONLY through the
    // current firm's tenant-scoped query above. A userId-only query cannot be
    // proven against the active-membership rules and is denied outright; a
    // firm-constrained variant would be a strict subset of the query above.

    // Additional client-side filtering
    let filteredTasks = tasks;

    if (filters.assignedTo) {
      filteredTasks = filteredTasks.filter(task => 
        task.assignedTo.toLowerCase().includes(filters.assignedTo.toLowerCase())
      );
    }

    if (filters.priority) {
      filteredTasks = filteredTasks.filter(task => task.priority === filters.priority);
    }

    return {
      success: true,
      tasks: filteredTasks
    };
  } catch (error) {
    console.error('Error getting enhanced tasks:', error);
    return {
      success: false,
      error: error.message,
      tasks: []
    };
  }
};

// Update Enhanced Task
export const updateEnhancedTask = async (taskId, updates) => {
  try {
    const taskRef = doc(db, 'tasks', taskId);

    // Write-boundary enforcement — but only when dueTime is ACTUALLY
    // changing. An edit form resubmits the whole task object regardless
    // of what the user touched, so "is dueTime a key in updates" alone
    // isn't enough to tell "the user is changing the time" apart from
    // "the time was just along for the ride" — comparing against the
    // CURRENT stored value is what lets an unrelated edit (title, status,
    // assignee) on an existing legacy/malformed record go through
    // untouched, while a real change to dueTime is still enforced. See
    // utils/dueTimeValidation.js.
    if ('dueTime' in updates) {
      const currentSnap = await getDoc(taskRef);
      const currentDueTime = currentSnap.exists() ? currentSnap.data().dueTime : undefined;
      if (updates.dueTime !== currentDueTime) {
        const dueTimeCheck = enforceCanonicalDueTime(updates.dueTime);
        if (!dueTimeCheck.ok) {
          return { success: false, error: dueTimeCheck.error };
        }
        updates = { ...updates, dueTime: dueTimeCheck.value };
      }
    }

    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    // If marking as completed, add completedAt timestamp
    if (updates.status === 'completed' && !updates.completedAt) {
      updateData.completedAt = Timestamp.now();
    }
    
    await updateDoc(taskRef, updateData);
    
    return {
      success: true,
      taskId
    };
  } catch (error) {
    console.error('Error updating enhanced task:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete Enhanced Task
export const deleteEnhancedTask = async (taskId) => {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    await deleteDoc(taskRef);
    
    return {
      success: true,
      taskId
    };
  } catch (error) {
    console.error('Error deleting enhanced task:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get Upcoming Tasks (next N days)
export const getUpcomingEnhancedTasks = async (userId, days = 7) => {
  try {
    const result = await getEnhancedTasks(userId, { status: 'pending' });
    
    if (!result.success) {
      return result;
    }
    
    // Africa/Accra business-time semantics (utils/firmassistTime.js) — the
    // viewer's own device timezone must not change which tasks count as
    // "upcoming".
    const upcomingTasks = result.tasks.filter(task => isUpcomingAccra(task.dueDate, new Date(), days));
    
    return {
      success: true,
      tasks: upcomingTasks
    };
  } catch (error) {
    console.error('Error getting upcoming enhanced tasks:', error);
    return {
      success: false,
      error: error.message,
      tasks: []
    };
  }
};

// Get Overdue Tasks
export const getOverdueTasks = async (userId) => {
  try {
    const result = await getEnhancedTasks(userId, { status: 'pending' });
    
    if (!result.success) {
      return result;
    }
    
    // Africa/Accra business-time semantics (utils/firmassistTime.js)
    const overdueTasks = result.tasks.filter(task => isOverdue(task));
    
    return {
      success: true,
      tasks: overdueTasks
    };
  } catch (error) {
    console.error('Error getting overdue tasks:', error);
    return {
      success: false,
      error: error.message,
      tasks: []
    };
  }
};

// Format task for display
export const formatTaskDisplay = (task) => {
  const dateStr = formatAccraDate(task.dueDate, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US');

  // A missing/malformed dueDate must never render as a blank/dangling
  // string (e.g. just " at 3:00 PM" with no date before it) — say so
  // explicitly instead.
  let displayString = dateStr || 'Due date not set';
  
  if (task.dueTime) {
    // Convert 24h time to 12h format
    const [hours, minutes] = task.dueTime.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    displayString += ` at ${displayHour}:${minutes} ${ampm}`;
  }
  
  if (task.court) {
    displayString += ` - ${task.court}`;
  } else if (task.location) {
    displayString += ` - ${task.location}`;
  }
  
  if (task.judge) {
    displayString += ` (Justice ${task.judge})`;
  }
  
  return displayString;
};

// Get event type icon/color
export const getEventTypeStyle = (eventType) => {
  const styles = {
    task: {
      color: '#10b981',
      label: 'Task',
      icon: '✓'
    },
    court_date: {
      color: '#ef4444',
      label: 'Court Date',
      icon: '⚖️'
    },
    deadline: {
      color: '#f59e0b',
      label: 'Deadline',
      icon: '⏰'
    }
  };
  
  return styles[eventType] || styles.task;
};

const EnhancedTaskService = {
  createEnhancedTask,
  getEnhancedTasks,
  updateEnhancedTask,
  deleteEnhancedTask,
  getUpcomingEnhancedTasks,
  getOverdueTasks,
  formatTaskDisplay,
  getEventTypeStyle
};
export default EnhancedTaskService;
